"use client";
import reactHtmlParser, { attributesToProps, domToReact } from "html-react-parser";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useContext, useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import classNames from "classnames";
import camelCase from "lodash/camelCase";
import kebabCase from "lodash/kebabCase";
import isEmpty from "lodash/isEmpty";

import { useAuth0V2 } from "@stadium/services/auth0";

import Button from "@stadium/shared/src/components/shared/button";
import Tooltip from "@stadium/ui/src/components/tooltip/tooltip";
import MultiCarousel from "@stadium/ui/src/components/multi-carousel";
import SmoothMobileCarouselWrapper from "@stadium/ui/src/components/multi-carousel/smooth-mobile-carousel-wrapper";
import RadioInput from "@stadium/ui/src/components/form/RadioInput";
import Label from "@stadium/ui/src/components/form/Label";
import { useJuneSoEventTracking } from "@stadium/services/june-analytics/hooks/use-june-so-event-tracking";
import BookACallForm from "components/book-a-call/form";
import PurchasePackageBtn from "components/onboarding-packages/purchase-package";
import FeatureDetailModal from "@stadium/shared/src/components/onboarding-packages/details/feature-detail-modal";
import RecognitionBanner from "@stadium/shared/src/components/recognition-banner";

import { Spinner } from "@stadium/ui/src/components/loaders/loaders";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { formatPrice } from "@stadium/helpers/currency";
import { useConfig } from "@stadium/hooks/lib/use-config";
import usePackage from "@stadium/hooks/lib/use-package";
import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user/current-user-provider";
import { useFlags, withSyncFeatureFlag } from "@stadium/services/launch-darkly";

import { getBookACallCtaText } from "@stadium/shared/src/selectors/book-a-call";
import { useBookACallText } from "@stadium/shared/src/hooks/book-a-call";

import { JuneSoContext, useJuneSoComponentContext } from "@stadium/services/june-analytics";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { DASHBOARD_ROUTE } from "@stadium/shared/src/constants/routes";
import { DEFAULT_CURRENCY_CODE } from "@stadium/constants";
import {
  FEATURE_PACKAGES,
  FEATURE_PACKAGES_SERVICES,
  GET_STARTED_FOR_FREE,
  PURCHASE_CTA,
  TALK_TO_SALES,
} from "constants/june-so";
import { SIGN_UP_PAGE_PARAM } from "@stadium/constants/routes";
import { BYSTADIUM_BOOK_A_CALL } from "@stadium/helpers/src/app-routes";
import { workspacePermissionFlag } from "@stadium/services/launch-darkly/constants";

import formImage from "@stadium/shared/src/assets/images/character-meeting.png";

import style from "./main.module.scss";

const RECOMMENDED_FOR_KEY = "recommendedFor";
const PACKAGE_PRICE_KEY = "price";
const CUSTOM_PRICING_LABLEL = "Custom";
const ON_DEMAND_SWAG_DESIGNS_KEY = "onDemandSwagDesigns";
const SERVICE_FEE_KEY = "serviceFee";
const ADDITIONAL_FEATURES_KEY = "additionalFeatures";

const getReactParserOptions = ({
  style,
  isMobileDevice,
  isTabletDevice,
  handleModalBookACallClick = null,
  tooltipInteractive = true,
}) => ({
  replace(domNode) {
    if ("attribs" in domNode) {
      if (domNode.attribs && domNode.name === "tooltip") {
        const props = attributesToProps(domNode.attribs);
        return (
          <Tooltip
            arrow
            interactive={tooltipInteractive}
            isLightTheme
            heading={(props?.header || "") as string}
            enablePortal
            content={props?.content || ""}
            placement={!isMobileDevice && !isTabletDevice ? "bottom" : "bottom-end"}
            childrenWrapperClassName={style.tipIconParent}
            containerClass={style.pckgTipConatiner}
            iconClassName={style.tooltipInfoIcon}
            asResponsiveModal
            infoIcon
            styles={style}
          />
        );
      }
      if (domNode.attribs && domNode.name === "modal") {
        const props = attributesToProps(domNode.attribs);
        return (
          <FeatureDetailModal
            header={props?.header as string}
            content={props?.content as string}
            handleModalBookACallClick={handleModalBookACallClick}
          >
            {domToReact(domNode?.children)}
          </FeatureDetailModal>
        );
      }
    }
  },
});

const OnboardingPackagesDetails = ({
  isPurchasing,
  pkgCotntainerClass = "",
  handleGetStartedClick = null,
  sourceUrl = "",
  packageDetails,
  packagePermissions,
  showBookACall = true,
  selectedPackageId = null,
  setSelectedPackageId,
  filteredPackageIds,
  showPurchaseCtas = true,
  filteredPackageTableLabelsEnabled = false,
  showRecognitionBanner = true,
  activePackage,
}) => {
  const { push } = useRouter();
  const talkToSalesCtaText = useConfig(getBookACallCtaText);
  const bookACallCtaText = useBookACallText({ defaultText: "BOOK A DEMO" });

  const { [workspacePermissionFlag]: workspacePermissionFlagEnabled } = useFlags();

  const { isMobileDevice, isTabletDevice, currentDevice } = useContext(CurrentDeviceContext);
  const { currentUserData } = useCurrentUserContext();
  const { isAuthenticated, isLoading } = useAuth0V2();

  const { componentName } = useJuneSoComponentContext();
  const { trackEvent } = useJuneSoEventTracking({ services: FEATURE_PACKAGES_SERVICES });
  const { isJuneSoScriptLoaded } = useContext(JuneSoContext);
  const [tableLabelsHeight, setTableLabelsHeight] = useState({});
  const [forceUpdate, setForceUpdate] = useState(0);

  const packageRefs = useRef({});

  const newPackageData = useMemo(
    () => ({
      packageDetails,
      packagePermissions,
    }),
    [],
  );

  const {
    packages: packagesMap,
    userPackageData,
    loading,
    isPackageListLoading,
  } = usePackage(newPackageData);

  const packageTableLabels = useMemo(
    () => Object.values(packagesMap || {})[0]?.orderedPreferenceKeys?.map(camelCase) || [],
    [packagesMap],
  );

  const { currentPackage: currentPackageInfo } = userPackageData;

  const userActivePackage = workspacePermissionFlagEnabled ? activePackage : currentPackageInfo;

  const packages = useMemo(
    () =>
      Object.values(packagesMap || {})
        .map((pkg) => {
          const price = (() => {
            if (pkg.customPricing) return CUSTOM_PRICING_LABLEL;
            return formatPrice(pkg.price, { currencyCode: DEFAULT_CURRENCY_CODE });
          })();
          return { ...pkg, oneTimePrice: pkg.customPricing, price };
        })
        .sort((a, b) => a.position - b.position),
    [packagesMap],
  );

  const filteredPackageTableLabels = useMemo(() => {
    if (!filteredPackageTableLabelsEnabled || !packages.length || !packageTableLabels.length)
      return packageTableLabels;

    return packageTableLabels.filter((dataKey) => {
      if (dataKey === "serviceFee") {
        return false;
      }
      // Check if at least one package has a truthy value for this dataKey
      return packages.some((pkg) => {
        const value = pkg[dataKey];
        return !isEmpty(value);
      });
    });
  }, [packages, packageTableLabels, filteredPackageTableLabelsEnabled]);

  const isResponsiveView = isMobileDevice || isTabletDevice;

  useEffect(() => {
    if (componentName && isJuneSoScriptLoaded && !isLoading) {
      trackEvent({ componentName, elementPosition: FEATURE_PACKAGES, action: "viewed" });
    }
  }, [componentName, isJuneSoScriptLoaded, isLoading]);

  useEffect(() => {
    setTimeout(() => {
      setForceUpdate((prev) => prev + 1);
    }, 10);
  }, [packages]);

  useLayoutEffect(() => {
    if (packages?.length && Object.keys(packageRefs.current).length && !isPackageListLoading) {
      const updatedHeights = {};
      filteredPackageTableLabels.forEach((dataKey) => {
        const refs = packageRefs.current[dataKey].filter(Boolean) || [];
        const maxHeight = Math.max(...refs.map((ref) => ref.clientHeight || 0));
        updatedHeights[dataKey] = maxHeight;
      });

      setTableLabelsHeight(updatedHeights);
    }
  }, [
    currentDevice,
    filteredPackageTableLabels,
    packages,
    filteredPackageIds,
    isPackageListLoading,
    forceUpdate,
  ]);

  const handleTalkToSalesClick = ({ name }) => {
    const bookACallForm = document.getElementById("book-a-call");

    if (bookACallForm) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("interestedPackage", name);
      window.history.replaceState({}, "", currentUrl.toString());
      return bookACallForm.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
    }
    return push(`${BYSTADIUM_BOOK_A_CALL}?interestedPackage=${name}`);
  };

  const rowClass = {
    price: style.price,
    recommendedFor: style.recommendedFor,
    commonFeatures: style.commonFeatures,
    onDemandSwagDesigns: style.onDemandSwagDesigns,
    additionalFeatures: style.additionalFeatures,
    onboarding: style.onboarding,
    support: style.support,
    netTerms: style.netTerms,
    recipientSupport: style.recipientSupport,
  };

  const renderPurchaseCta = ({ id, default: isDefault, name, slug, customPricing }) => {
    const juneSoDefaultProps = {
      elementPosition: kebabCase(name.toLowerCase()),
      services: FEATURE_PACKAGES_SERVICES,
    };
    if (isLoading || loading) {
      return <Spinner className={style.pkgLoaderCta} />;
    }
    if (userActivePackage?.id === id && isAuthenticated && !isDefault)
      return <div className={style.currentPckgText}>Your Current Package</div>;
    if (isDefault)
      return (
        <Button
          onClick={() => {
            if (handleGetStartedClick && isAuthenticated) {
              return handleGetStartedClick();
            } else {
              const unAuthenticatedLinkSearchParams = `?${SIGN_UP_PAGE_PARAM}`;
              push(
                `${sourceUrl || DASHBOARD_ROUTE}${
                  isAuthenticated ? "" : unAuthenticatedLinkSearchParams
                }`,
              );
            }
          }}
          styleType="primary"
          className={style.purchaseBtn}
          title="Get Started for Free"
          juneSoProps={{
            ...juneSoDefaultProps,
            target: GET_STARTED_FOR_FREE,
          }}
        >
          Get Started for Free
        </Button>
      );
    const purchasePackageJuneSoProps = {
      ...juneSoDefaultProps,
      target: PURCHASE_CTA,
    };

    return (
      <>
        <Button
          juneSoProps={{ ...juneSoDefaultProps, target: TALK_TO_SALES }}
          styleType="primary"
          className={style.expertSpeakBtn}
          onClick={() => handleTalkToSalesClick({ name })}
          title={talkToSalesCtaText}
        >
          {talkToSalesCtaText}
        </Button>
        {!customPricing && !currentUserData?.isActivePackageAdmin && (
          <PurchasePackageBtn
            packageSlug={slug}
            packageName={name}
            containerClass={style.purchaseBtn}
            juneSoProps={purchasePackageJuneSoProps}
          />
        )}
      </>
    );
  };

  const renderServiceFee = (pkg) => {
    if (pkg.serviceFee) {
      return (
        <>
          {reactHtmlParser(pkg.serviceFee)}
          {pkg.serviceFeeTooltip && (
            <div className={style.pricingHeaderInfoIcon}>
              {reactHtmlParser(
                pkg.serviceFeeTooltip,
                getReactParserOptions({
                  style,
                  isMobileDevice,
                  isTabletDevice,
                  tooltipInteractive: false,
                }),
              )}
            </div>
          )}
        </>
      );
    }
    return "No Service Fee";
  };

  const renderOldSubHeader = (pkg) =>
    Number(pkg.monthlyPrice) ? (
      <div className={style.anualText}>Billed Annually</div>
    ) : (
      <div className={style.anualText}>No Subscription Required</div>
    );
  const renderSubHeader = (pkg) =>
    filteredPackageTableLabelsEnabled ? (
      <>
        <div className={`${style.annualContract} ${style.anualText} ${style.anualTextV2}`}>
          {pkg?.default ? "No Contract" : "Annual Contract"}
        </div>
        <div className={style.noServiceFee}>{renderServiceFee(pkg)}</div>
      </>
    ) : (
      renderOldSubHeader(pkg)
    );

  const renderPkgTableHeader = (pkg) => (
    <div
      key={`pkg-header-${pkg.id}`}
      className={`${style.columnBlock} ${style.packageCol} ${pkg.name.toLowerCase()} ${
        filteredPackageTableLabelsEnabled ? style.packageColV2 : ""
      }`}
    >
      <div
        className={`${style.infoColBlock} ${
          filteredPackageTableLabelsEnabled ? style.infoColBlockV2 : ""
        }`}
      >
        <span className={style.packageImgHolder}>
          <Multimedia
            type="image"
            alt={`${pkg.name} image`}
            src={pkg.iconImageUrl}
            isNextImage={false}
            className={style.packageImg}
            width={30}
            height={30}
          />
        </span>
        <div
          className={`${style.pckgHeader} ${
            filteredPackageTableLabelsEnabled ? style.pckgHeaderV2 : ""
          }`}
          style={{ color: pkg.colorCodeDark }}
        >
          <span className={style.packageName}>{pkg.name}</span>
        </div>
        <div
          className={`${style.packageTitle} ${
            filteredPackageTableLabelsEnabled ? style.packageTitleV2 : ""
          }`}
        >
          <div
            className={`${style.packagePriceHolder} ${
              filteredPackageTableLabelsEnabled ? style.packagePriceHolderV2 : ""
            }`}
            style={{ color: pkg.colorCodeDark, backgroundColor: pkg?.colorCodeBg }}
          >
            {pkg?.popular && <span className={style.priceHolderTag}>Popular</span>}
            {pkg.oneTimePrice ? (
              <>
                <div
                  className={`${style.pricePackageContentHolder} ${
                    filteredPackageTableLabelsEnabled ? style.pricePackageContentHolderV2 : ""
                  }`}
                >
                  <span
                    className={`${style.priceText} ${
                      filteredPackageTableLabelsEnabled ? style.priceTextV2 : ""
                    }`}
                  >
                    {pkg.price}
                  </span>
                </div>
                {filteredPackageTableLabelsEnabled && (
                  <>
                    <div
                      className={`${style.annualContract} ${style.anualText} ${style.anualTextV2}`}
                    >
                      Annual Contract
                    </div>
                    <div className={style.noServiceFee}>No Service Fee</div>
                  </>
                )}
              </>
            ) : (
              <div className={style.pricePackageContentHolder}>
                <span
                  className={`${style.priceText} ${
                    filteredPackageTableLabelsEnabled ? style.priceTextV2 : ""
                  }`}
                >
                  {formatPrice(pkg.monthlyPrice, { currencyCode: DEFAULT_CURRENCY_CODE })}
                </span>
                <span className={style.yearText}>/Month</span>
                {renderSubHeader(pkg)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPkgTableFooter = (pkg) => (
    <div
      key={`pkg-footer-${pkg.id}`}
      className={classNames(
        style.columnBlock,
        style.packageCol,
        style.packageFooterCol,
        style.packageFooterTextLink,
        pkg.name.toLowerCase(),
        {
          [style.packageFooterColV2]: filteredPackageTableLabelsEnabled,
          [style.packageColV2]: filteredPackageTableLabelsEnabled,
        },
      )}
    >
      {renderPurchaseCta(pkg)}
    </div>
  );

  const renderFeatureLabelInfo = (pkg, dataKey) => {
    const showRecommendForTooltip = dataKey === RECOMMENDED_FOR_KEY && pkg.recommendedForTooltip;
    const showOnDemandSwagDesignsTooltip =
      dataKey === ON_DEMAND_SWAG_DESIGNS_KEY && pkg.onDemandSwagDesignsTooltip;
    const showServiceFeeTooltip = dataKey === SERVICE_FEE_KEY && pkg.serviceFeeTooltip;
    const showAdditionalFeaturesTooltip =
      dataKey === ADDITIONAL_FEATURES_KEY && pkg.additionalFeaturesTooltip;
    const isPriceRow = dataKey === PACKAGE_PRICE_KEY;

    const showTooltip =
      showRecommendForTooltip ||
      showOnDemandSwagDesignsTooltip ||
      showServiceFeeTooltip ||
      showAdditionalFeaturesTooltip;

    const tooltipContent = (() => {
      if (showRecommendForTooltip) {
        return pkg.recommendedForTooltip;
      }
      if (showAdditionalFeaturesTooltip) {
        return pkg.additionalFeaturesTooltip;
      }
      if (showOnDemandSwagDesignsTooltip) {
        return pkg.onDemandSwagDesignsTooltip;
      }
      if (showServiceFeeTooltip) {
        return pkg.serviceFeeTooltip;
      }
      return "";
    })();

    if (isPriceRow) return null;

    const pkgData = pkg[dataKey];

    return (
      <div
        key={`pkg-${dataKey}-${pkg.id}`}
        className={classNames(style.columnBlock, style.columnPlanInfoBlock, {
          [style.columnPlanInfoBlockV2]: filteredPackageTableLabelsEnabled,
        })}
      >
        <div
          className={`${style.infoColBlock} ${
            filteredPackageTableLabelsEnabled ? style.infoColBlockV2 : ""
          }`}
        >
          <div className={style.infoColText}>
            {showTooltip &&
              reactHtmlParser(
                tooltipContent,
                getReactParserOptions({
                  style,
                  isMobileDevice,
                  isTabletDevice,
                }),
              )}
            {pkgData &&
              reactHtmlParser(
                pkgData,
                getReactParserOptions({
                  style,
                  isMobileDevice,
                  isTabletDevice,
                  handleModalBookACallClick: () => handleTalkToSalesClick({ name: pkg.name }),
                }),
              )}
          </div>
        </div>
      </div>
    );
  };

  const renderCarouselInfoCell = (dataKey, pkg, colNum) => {
    const isPriceRow = dataKey === PACKAGE_PRICE_KEY;

    if (isPriceRow) return null;
    return (
      <div
        key={dataKey}
        className={`${style.tocRow} ${rowClass[dataKey]} ${dataKey}`}
        ref={(ref) => {
          if (ref) {
            if (!packageRefs.current[dataKey]) {
              packageRefs.current[dataKey] = [ref];
            }
            packageRefs.current[dataKey][colNum] = ref;
          }
        }}
        style={tableLabelsHeight[dataKey] ? { height: `${tableLabelsHeight[dataKey]}px` } : {}}
      >
        {renderFeatureLabelInfo(pkg, dataKey)}
      </div>
    );
  };

  const renderPackageData = (pkg, colNum) => {
    return (
      <div
        className={`${style.slideItem} ${pkg.id == userActivePackage?.id ? style.purchased : ""}`}
        key={pkg.id}
      >
        {renderPkgTableHeader(pkg)}
        {!filteredPackageTableLabelsEnabled && showPurchaseCtas && renderPkgTableFooter(pkg)}
        {filteredPackageTableLabels.map((dataKey) => renderCarouselInfoCell(dataKey, pkg, colNum))}
        {filteredPackageTableLabelsEnabled && showPurchaseCtas && renderPkgTableFooter(pkg)}
      </div>
    );
  };

  const renderSpeakWithExpertNote = () => {
    if (!showBookACall || isPurchasing) {
      /* this note should be present if it's not there in page header */
      return null;
    }
    return (
      <div id="book-a-call" className={style.bookCallSection}>
        <div className={style.bookCallBlockImageContainer}>
          <div className={style.bookCallSectionTitle}>{bookACallCtaText}</div>
          <Multimedia
            src={formImage}
            alt={bookACallCtaText}
            type="image"
            className={style.bookCallBlockImage}
          />
        </div>
        <BookACallForm
          styles={style}
          showTitle={false}
          packagePermissions={packagePermissions}
          packageDetails={packageDetails}
        />
      </div>
    );
  };

  const renderContent = () =>
    packages.map((pkg, idx) => {
      if (filteredPackageIds && !filteredPackageIds.includes(pkg.id)) {
        return null;
      }

      return (
        <RadioInput
          id={pkg.id}
          key={pkg.id}
          name="package"
          className={style.radioInputField}
          classes={style.packageRadioInput}
          checked={pkg.id === selectedPackageId}
          disabled={!selectedPackageId}
          value={pkg.id}
          onChange={({ value }) => {
            setSelectedPackageId(value);
          }}
          label={
            <Label showIcon={false} className={style.packageRadioLabel}>
              {renderPackageData(pkg, idx)}
            </Label>
          }
        />
      );
    });

  const renderResponsiveView = () => (
    <SmoothMobileCarouselWrapper>
      <MultiCarousel
        infinite={!selectedPackageId}
        showDots
        responsive={{
          tablet: {
            breakpoint: { max: 1023, min: 768 },
            slidesToSlide: 1,
            items: 2,
          },
          mobile: {
            breakpoint: { max: 767, min: 0 },
            slidesToSlide: 1,
            items: 1,
            partialVisibilityGutter: 60,
          },
        }}
        partialVisible={isMobileDevice}
        centerMode={isTabletDevice}
        arrows={false}
      >
        {renderContent()}
      </MultiCarousel>
    </SmoothMobileCarouselWrapper>
  );

  const renderWebView = () => {
    return (
      <>
        {!isResponsiveView && (
          <div className={style.responsivePackageLoader}>
            <Spinner />
          </div>
        )}
        <div
          className={`${style.webViewPackageContainer} ${style.packageWrapperSpacing} ${pkgCotntainerClass}`}
        >
          {renderContent()}
        </div>
      </>
    );
  };
  return (
    <div
      className={`pricing-wrapper ${style.tocWrapper} ${
        isResponsiveView ? style.mobileView : style.webView
      } ${style.topSpacingUpdate}`}
    >
      {!packages?.length && <Spinner className={style.pkgLoader} />}
      {isResponsiveView ? renderResponsiveView() : renderWebView()}
      {filteredPackageTableLabelsEnabled && showRecognitionBanner && <RecognitionBanner />}
      <div className={style.pckgFooter}>{renderSpeakWithExpertNote()}</div>
    </div>
  );
};

export const ServerSideOnboardingPackagesDetails = OnboardingPackagesDetails;

export default withSyncFeatureFlag(OnboardingPackagesDetails, {
  loader: (
    <div className={style.loaderWrapper}>
      <Spinner />
    </div>
  ),
});
